import { BookOutlined, DeleteOutlined, SendOutlined } from '@ant-design/icons';
import { useLazyQuery, useMutation } from '@apollo/client';
import {
  Button,
  Card,
  Empty,
  Input,
  message,
  Popconfirm,
  Select,
  Spin,
  Tag,
  Typography,
} from 'antd';
import { Routes } from 'config/routes';
import { format } from 'date-fns';
import { ContactType, useGetDonorQuery } from 'gql/types';
import { ScreenSpinner } from 'modules/common/components';
import { FC, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useCurrentUser } from '../../../user/hooks/useCurrentUser';
import {
  CREATE_DONOR_CONTACT_MUTATION,
  DELETE_DONOR_CONTACT_MUTATION,
} from '../../graphql/mutations';
import { GET_DONOR_CONTACTS_QUERY } from '../../graphql/queries';

const { Title, Text } = Typography;

const CONTACT_TYPE_OPTIONS = [
  { value: ContactType.Phone, label: 'Phone' },
  { value: ContactType.Email, label: 'Email' },
  { value: ContactType.Text, label: 'Text' },
  { value: ContactType.InPerson, label: 'In Person' },
  { value: ContactType.Voicemail, label: 'Voicemail' },
  { value: ContactType.Other, label: 'Other' },
];

const CONTACT_TYPE_COLORS: Record<string, string> = {
  Phone: 'blue',
  Email: 'cyan',
  Text: 'green',
  InPerson: 'purple',
  Voicemail: 'orange',
  Other: 'default',
};

function parseDate(raw: string): string {
  try {
    const d = /^\d+$/.test(raw) ? new Date(parseInt(raw, 10)) : new Date(raw);
    return isNaN(d.getTime()) ? raw : format(d, 'MM/dd/yyyy h:mm a');
  } catch {
    return raw;
  }
}

const DonorCommunications: FC = () => {
  const pageParams = useParams();
  const navigate = useNavigate();
  const currentUser = useCurrentUser();
  const isAdmin = currentUser.data?.role?.value === 'Admin';
  const donorShortId = pageParams.id || '';

  const [noteText, setNoteText] = useState('');
  const [contactType, setContactType] = useState<ContactType>(
    ContactType.Phone
  );

  const { data: donorData, loading: donorLoading } = useGetDonorQuery({
    variables: { input: { id: donorShortId } },
    onError: (err) => {
      message.error(err.message);
      if (err.message === 'Not Found') {
        navigate(Routes.NotFound);
        return;
      }
      navigate(Routes.IndividualDonors);
    },
  });

  const donorId = donorData?.getDonor?.id;

  const [fetchContacts, { data: contactsData, loading: contactsLoading }] =
    useLazyQuery(GET_DONOR_CONTACTS_QUERY, {
      fetchPolicy: 'network-only',
    });

  const [createContact, { loading: saving }] = useMutation(
    CREATE_DONOR_CONTACT_MUTATION
  );

  const [deleteContact] = useMutation(DELETE_DONOR_CONTACT_MUTATION);

  useEffect(() => {
    if (donorId) {
      fetchContacts({ variables: { donorId } });
    }
  }, [donorId, fetchContacts]);

  const contacts = (contactsData?.getDonorContacts ?? []).filter(
    (c: { contactType: string }) => c.contactType !== 'Flag'
  );

  const handleSave = async () => {
    if (!noteText.trim() || !donorId) return;
    await createContact({
      variables: {
        input: {
          donorId,
          contactType,
          notes: noteText.trim(),
        },
      },
    });
    setNoteText('');
    message.success('Contact note saved');
    fetchContacts({ variables: { donorId } });
  };

  if (donorLoading) return <ScreenSpinner />;

  return (
    <div style={{ padding: '0 16px' }}>
      {/* Add new contact form */}
      <Card
        size="small"
        style={{ marginBottom: 16 }}
        title={
          <span>
            <BookOutlined style={{ marginRight: 8, color: '#1565c0' }} />
            New Contact Entry
          </span>
        }
      >
        <div
          style={{
            display: 'flex',
            gap: 8,
            marginBottom: 8,
            flexWrap: 'wrap',
            alignItems: 'flex-end',
          }}
        >
          <div>
            <Text
              type="secondary"
              style={{ fontSize: 12, display: 'block', marginBottom: 4 }}
            >
              Contact Method
            </Text>
            <Select
              value={contactType}
              onChange={setContactType}
              options={CONTACT_TYPE_OPTIONS}
              style={{ width: 140 }}
            />
          </div>
          <Button
            type="primary"
            icon={<SendOutlined />}
            loading={saving}
            disabled={!noteText.trim()}
            onClick={handleSave}
            style={{}}
          >
            Save Note
          </Button>
        </div>
        <Input.TextArea
          rows={3}
          value={noteText}
          onChange={(e) => setNoteText(e.target.value)}
          placeholder="Enter contact details..."
          onPressEnter={(e) => {
            if (!e.shiftKey) {
              e.preventDefault();
              handleSave();
            }
          }}
        />
      </Card>

      {/* Contact log list */}
      <Title level={5} style={{ marginBottom: 12 }}>
        Contact History
      </Title>

      {contactsLoading ? (
        <div style={{ textAlign: 'center', padding: 32 }}>
          <Spin />
        </div>
      ) : contacts.length === 0 ? (
        <Empty
          description="No communication notes yet"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      ) : (
        contacts.map(
          (c: {
            id: string;
            contactType: string;
            notes: string | null;
            createdAt: string;
            user: { id: string; firstName: string; lastName: string };
          }) => (
            <Card
              key={c.id}
              size="small"
              style={{ marginBottom: 8 }}
              bodyStyle={{ padding: '12px 16px' }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: 4,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Tag color={CONTACT_TYPE_COLORS[c.contactType] || 'default'}>
                    {c.contactType}
                  </Tag>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {parseDate(c.createdAt)}
                  </Text>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {c.user.firstName} {c.user.lastName}
                  </Text>
                  {isAdmin && (
                    <Popconfirm
                      title="Delete this contact note?"
                      onConfirm={async () => {
                        await deleteContact({ variables: { id: c.id } });
                        message.success('Contact note deleted');
                        fetchContacts({ variables: { donorId } });
                      }}
                      okText="Delete"
                      cancelText="Cancel"
                      okButtonProps={{ danger: true }}
                    >
                      <DeleteOutlined
                        style={{
                          color: '#ff4d4f',
                          cursor: 'pointer',
                          fontSize: 13,
                        }}
                      />
                    </Popconfirm>
                  )}
                </div>
              </div>
              <div style={{ fontSize: 13, color: '#2f3a45' }}>
                {c.notes || '—'}
              </div>
            </Card>
          )
        )
      )}
    </div>
  );
};

export default DonorCommunications;
